import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Tooltip,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useAppSelector } from "../../hooks/hooks";

interface Voucher {
  id: string;
  name: string;
  image: string;
  amount: number;
  currency: string;
  purchaseDate: string;
  recipientEmail?: string;
  status: "available" | "pending" | "sent";
}

const GiftsSection: React.FC = () => {
  const { vouchers } = useAppSelector((state) => state.giftBrands);
  // Mock data - in a real app this would come from Redux or API
  //   const [vouchers, setVouchers] = useState<Voucher[]>([
  //     {
  //       id: "1",
  //       name: "Amazon Gift Card",
  //       image: "https://logo.clearbit.com/amazon.com",
  //       amount: 50,
  //       currency: "USD",
  //       purchaseDate: "2023-05-15",
  //       status: "available",
  //     },
  //     {
  //       id: "2",
  //       name: "Netflix Subscription",
  //       image: "https://logo.clearbit.com/netflix.com",
  //       amount: 15,
  //       currency: "USD",
  //       purchaseDate: "2023-06-01",
  //       status: "available",
  //     },
  //     {
  //       id: "3",
  //       name: "Spotify Premium",
  //       image: "https://logo.clearbit.com/spotify.com",
  //       amount: 10,
  //       currency: "USD",
  //       purchaseDate: "2023-06-10",
  //       recipientEmail: "friend@example.com",
  //       status: "sent",
  //     },
  //   ]);

  const [sendingId, setSendingId] = useState<string | null>(null);
  const [recipientEmail, setRecipientEmail] = useState("");

  const handleStartSending = (id: string) => {
    setSendingId(id);
    setRecipientEmail("");
  };

  const handleCancelSending = () => {
    setSendingId(null);
    setRecipientEmail("");
  };

  const handleSendVoucher = (id: string) => {
    if (!recipientEmail) return;

    // setVouchers(
    //   vouchers.map((voucher) =>
    //     voucher.id === id
    //       ? { ...voucher, recipientEmail, status: "sent" }
    //       : voucher
    //   )
    // );

    setSendingId(null);
    setRecipientEmail("");

    // In a real app, you would dispatch an action here
    // dispatch(sendVoucher({ id, recipientEmail }));
  };

  const handleDeleteVoucher = (id: string) => {
    // setVouchers(vouchers.filter((voucher) => voucher.id !== id));
    // dispatch(deleteVoucher({ id }));
  };

  const getStatusChip = (status: Voucher["status"]) => {
    switch (status) {
      case "available":
        return <Chip label="Available" color="success" size="small" />;
      case "pending":
        return <Chip label="Pending" color="warning" size="small" />;
      case "sent":
        return <Chip label="Sent" color="info" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" gutterBottom>
          My Gift Vouchers
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Voucher</TableCell>
              <TableCell align="center">Amount</TableCell>
              <TableCell align="center">Purchase Date</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Recipient</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vouchers.map((voucher) => (
              <TableRow key={voucher.id}>
                {/* Voucher Column */}
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Avatar
                      src={voucher.image}
                      alt={voucher.brand}
                      sx={{ width: 32, height: 32, mr: 2 }}
                    />
                    <Typography variant="body2">{voucher.brand}</Typography>
                  </Box>
                </TableCell>

                {/* Amount Column */}
                <TableCell align="center">
                  {voucher.currency}
                  {voucher.amount.toFixed(2)}
                </TableCell>

                {/* Purchase Date Column */}
                <TableCell align="center">
                  {new Date(voucher.purchaseDate).toLocaleDateString()}
                </TableCell>

                {/* Status Column */}
                <TableCell align="center">
                  {/* {getStatusChip(voucher.status)} */}
                </TableCell>

                {/* Recipient Column */}
                <TableCell align="center">
                  {voucher.recipientEmail || "-"}
                </TableCell>

                {/* Actions Column */}
                <TableCell align="right">
                  {sendingId === voucher.id ? (
                    <>
                      <TextField
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        placeholder="Recipient email"
                        size="small"
                        sx={{ mr: 1, width: 200 }}
                      />
                      <Tooltip title="Send">
                        <IconButton
                          onClick={() => handleSendVoucher(voucher.id)}
                          disabled={!recipientEmail}
                        >
                          <CheckCircleIcon color="success" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton onClick={handleCancelSending}>
                          <CancelIcon color="error" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      {voucher.status === "available" && (
                        <Tooltip title="Send to someone">
                          <IconButton
                            onClick={() => handleStartSending(voucher.id)}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View details">
                        <IconButton>
                          <ReceiptIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {voucher.status === "available" && (
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={() => handleDeleteVoucher(voucher.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default GiftsSection;
